/**
 * `pageStartUnits` — DOM-measuring glue for `pageBreakModel` (RECUR-27304
 * Delta v3). Moved out of `usePageBoundaries.ts` so the SAME model output can
 * be consumed by BOTH the always-mounted overlay hook AND the server exporter
 * (`exportToPdfServer.ts`) without pulling React into the export bundle — NO
 * React import anywhere in this file.
 *
 * `pageBreakModel` returns page-start UNIT INDICES (flat atomic-unit
 * positions — one per top-level block, or one per rowspan-bound row GROUP
 * for a decomposed table (ordinarily one `<tr>` each — RECUR-27304
 * rowspan-safe pagination, see `computeRowGroups`); see `pageBreakModel.ts` /
 * `reindexGroupsForRows`). The private
 * `computePageStartIndices` runs that model once; this module projects its
 * ONE output two ways:
 *   • `computePageBoundaries(blocks, width)` -> the overlay's existing shape
 *     `{ boundaries: number[] (Ys), pageHeightPx }` — byte-identical output
 *     to the pre-Delta-v3 `usePageBoundaries.computePageBoundaries` (maps
 *     each start index to `geometry[i].offsetTop`).
 *   • `computePageStartUnits(contentEl)` -> the LIVE element(s) each
 *     page-start unit corresponds to, for `exportToPdfServer.ts` to mark with
 *     `data-pdf-page-break` (whole-block units) or split a cloned `<table>`
 *     at (row units) — see `PageStartUnit` below.
 *
 * Delta v4 (gap-display, RECUR-27304): this file works in TWO coordinate
 * spaces and every value below is documented as to which one it's in —
 * mixing them up is exactly the measurement-feedback-loop bug this delta
 * exists to avoid (a rendered gap shifts `offsetTop`, which would otherwise
 * shift the next computed gap, forever):
 *   • NORMALIZED (gap-free) — what `geometry` (fed to `pageBreakModel`) and
 *     `computePageStartUnits`' output are in. The CURRENT cumulative gap
 *     already painted on the live DOM (read back via `getInjectedGapPx`,
 *     never tracked as separate module state) is subtracted back out of
 *     every unit's `offsetTop` before the model ever sees it, so the model
 *     always reasons about the SAME layout it would if no gap had ever been
 *     applied — this is what makes re-running the whole pipeline on
 *     already-gapped geometry idempotent instead of oscillating, and is
 *     also why `computePageStartUnits` (the export projection) stays
 *     completely unaffected by whether `showPageGaps` happens to be on.
 *   • LIVE (post-gap) — what `computePageBoundaries`' `boundaries` are in:
 *     the ACTUAL on-screen Y, gap included, so `PageBoundaryOverlay`'s seam
 *     + chip land at the true start of the new page (right after the gap).
 *   • PAGE CHROME (RECUR-27604) — `reservedPageTopPx`, the optional per-page
 *     top space a PNO logo header consumes on the SERVER export. Threaded
 *     through every function below as a subtraction from page CAPACITY
 *     (`getServerPageHeightPx(width) - reservedPageTopPx`), PLUS the
 *     logo-branch page-top-margin reclaim added back whenever the band is
 *     reserved (`getLogoPageTopReclaimPx(width)` — see pageGeometry.ts's own
 *     module comment for why that reclaim exists: `getServerPageHeightPx` is
 *     calibrated at a 0.6in top margin, but the logo branch's exported page
 *     actually renders at 0.35in, so it physically holds more than the plain
 *     subtraction alone would claim). Neither term is ever added to any
 *     block's `offsetTop` in either coordinate space above — both are pure
 *     capacity adjustments. The overlay and the exporter both pass the SAME
 *     `reservedPageTopPx` value (see `usePnoReportLogoReservedPx`), and this
 *     module is the ONE shared place both read the reclaimed capacity from,
 *     so a page-start computed here always agrees between "where the editor
 *     draws the seam" and "where the PDF actually breaks" — but the editor
 *     deliberately never PAINTS the reserved space itself (no visual band
 *     today — see the plan's §3).
 */
import { getServerPageHeightPx, getLogoPageTopReclaimPx } from './pageGeometry';
import { buildPaginationGroups } from './exportToPdf';
import {
    pageBreakModelWithGaps,
    reindexGroupsForRowsAndLines,
    LINE_ORPHAN_WIDOW_MIN_LINES,
    OVERSIZED_ROW_SPLIT_RATIO,
    type BlockGeometry,
} from './pageBreakModel';

export interface PageBoundaries {
    /** Y position (CSS px, LIVE/post-gap coordinates — see module doc) of each interior page cut. */
    boundaries: number[];
    /**
     * Unused space (CSS px) left behind on the page ABOVE `boundaries[i]`
     * (RECUR-27304 gap-display) — parallel to `boundaries`. 0 when the
     * group simply fit with nothing to spare, AND for a mid-table 'row'
     * start: margin has no effect on a `<tr>`, so `applyPageGapStyles` never
     * renders a real gap there even though the model's own math is exact —
     * see `computePageGapTargets`, which is what actually decides what gets
     * painted in the DOM.
     */
    gaps: number[];
    /**
     * Page-start kind per boundary, parallel to `boundaries`/`gaps`
     * (RECUR-27304 Word-frame visuals) — mirrors `PageStartUnit.kind`. Only a
     * `'block'`-kind boundary has a live element margin can attach to, so
     * only it can ever receive a real DOM gap via `computePageGapTargets` /
     * `applyPageGapStyles`; a `'row'`/`'line'`-kind boundary (mid-table /
     * mid-paragraph split) never gets margin (CSS margin has no effect on a
     * `<tr>`, and a mid-paragraph split has no block-level box to attach to
     * either). `PageBoundaryOverlay` itself no longer reads this field
     * (RECUR-27304 "one consistent marker") — it renders the identical
     * seam + chip at every boundary regardless of kind. `'rowSegment'`
     * (RECUR-27604 §14, in-row split) is the mid-ROW analogue of `'line'` —
     * see `PageStartUnit`'s own doc for what it means.
     */
    kinds: Array<'block' | 'row' | 'line' | 'rowSegment'>;
    /** The SERVER page height boundaries were computed against (0 when width isn't measurable). */
    pageHeightPx: number;
}

/**
 * One page-start decision, mapped back onto the LIVE DOM element(s) it
 * corresponds to (RECUR-27304 Delta v3 — the export's injection unit).
 *   • `kind === 'block'` — `element` is the live top-level block that begins
 *     the page (a whole-block start, including a table whose FIRST row
 *     begins the page — nothing to split there, the whole table just moves).
 *     `blockIndex` is its position in `getTopLevelBlocks(contentEl)`; the
 *     exporter re-resolves that SAME position against `getTopLevelBlocks(clone)`
 *     (`cloneNode` preserves order 1:1) and marks the CLONE's block with
 *     `data-pdf-page-break` — the live element is never touched.
 *   • `kind === 'row'` — a MID-TABLE start: `tableBlock` is the live
 *     top-level block that IS/wraps the `<table>` (the same shape
 *     `getTopLevelBlocks` returns), `rowIndex` (always >= 1 — row 0 can only
 *     ever be part of a `'block'` start) is the FIRST `<tr>` index of the
 *     rowspan-bound row GROUP the break falls before (RECUR-27304
 *     rowspan-safe pagination — see `computeRowGroups`; ordinarily just the
 *     row itself for a plain table with no rowSpan) — the only row index a
 *     break may ever land on, so a spanning cell is never split across the
 *     export's physical table slice. `element` is that row's own `<tr>`. The
 *     exporter physically SPLITS the CLONED table there — it never marks a
 *     live `<tr>`. `blockIndex` is unset for row-kind units —
 *     `splitClonedTablesAtRows` matches the cloned table by element identity
 *     via `tableBlock`, not by index.
 *   • `kind === 'line'` — a MID-PARAGRAPH start (RECUR-27304 Delta v5, the
 *     paragraph analogue of `'row'`): `textBlock` is the live top-level `<p>`
 *     the break falls inside, `lineIndex` is the (0-based) rendered line the
 *     break falls BEFORE — by construction of `reindexGroupsForRowsAndLines`'
 *     orphan/widow grouping this is always >= 2 (line 0 is a `'block'`-kind
 *     start — "the whole paragraph moves", nothing to split there — and line
 *     1 can never be any group's OWN start). `element` is the SAME live `<p>`
 *     (there is no single DOM node representing "just this line" the way a
 *     `<tr>` represents a row). The exporter physically SPLITS the CLONED
 *     paragraph there (`splitClonedParagraphsAtLines`) — it never marks or
 *     mutates the live `<p>`. `blockIndex` is unset, same reasoning as `'row'`.
 *   • `kind === 'rowSegment'` — a MID-ROW start (RECUR-27604 §14, in-row
 *     split — the row analogue of `'line'`): a single-row group taller than
 *     `OVERSIZED_ROW_SPLIT_RATIO * pageHeightPx` decomposes at its PRIMARY
 *     cell's Slate-block (paragraph) boundaries — see `measureRowSegments`.
 *     `tableBlock`/`rowIndex` identify the row exactly like `'row'`;
 *     `cellIndex` is that row's primary cell's index, `cellChildIndex` the
 *     element-child index (counting Slate-block children only — see
 *     `getSlateCellChildren`) the cut falls BEFORE (always >= 1 — segment 0
 *     is the row's own `'block'`/`'row'`-kind start, nothing to split there).
 *     `element` is that live child itself. The exporter physically SPLITS
 *     the CLONED `<tr>` into two rows at this child
 *     (`splitOneClonedRowAtChild`) — it never marks or mutates the live
 *     `<tr>`. `blockIndex` is unset, same reasoning as `'row'`/`'line'`.
 */
export interface PageStartUnit {
    element: HTMLElement;
    offsetTop: number;
    kind: 'block' | 'row' | 'line' | 'rowSegment';
    tableBlock?: HTMLElement;
    rowIndex?: number;
    /** Live top-level `<p>` a `'line'`-kind start falls inside — see `PageStartUnit`'s own doc. */
    textBlock?: HTMLElement;
    /** 0-based rendered line index a `'line'`-kind start falls BEFORE — see `PageStartUnit`'s own doc. */
    lineIndex?: number;
    /** Position in `getTopLevelBlocks(contentEl)` — set only for `kind === 'block'`. */
    blockIndex?: number;
    /** `'rowSegment'` only — the row's PRIMARY cell's index (see `PageStartUnit`'s own doc). */
    cellIndex?: number;
    /** `'rowSegment'` only — the primary cell's Slate-block child index the cut falls BEFORE (see `PageStartUnit`'s own doc). */
    cellChildIndex?: number;
}

export const EMPTY_BOUNDARIES: PageBoundaries = { boundaries: [], gaps: [], kinds: [], pageHeightPx: 0 };

/**
 * One gap-application target for the live editor DOM (RECUR-27304
 * gap-display; extended RECUR-27604 §8 for row-kind hosts): a page-start
 * element paired with the vertical space to render at it, PLUS the metadata
 * `usePageBoundaries` needs to derive `bandTops` without re-deriving the
 * hostability predicate a second time (§8.5/N-5 — "one predicate, one
 * place"):
 *   • `kind === 'block'` — a top-level block; `gapPx` is the model's own
 *     leftover space for that page start (`core.startGaps[i]`, may be 0).
 *   • `kind === 'row'` — a mid-table `<tr>` (§8.1's cell-padding vehicle);
 *     `gapPx` is always 0 — the model's own row-kind leftover is
 *     deliberately never painted (§8.6), only the reservedPageTopPx slot is
 *     (see `usePageBoundaries.ts`'s `applyPageGapStyles`). Only emitted for a
 *     HOSTABLE row — one with no inline `height` of its own (§8.5); a row
 *     carrying one (`tableElements.tsx:162`) is skipped entirely, never
 *     appears here, and therefore never contributes a `bandTops` entry.
 * `boundaryIndex` is this target's position in `PageBoundaries.boundaries`/
 * `gaps`/`kinds` (i.e. `i` in `core.startUnitIndices`) — what
 * `usePageBoundaries` keys its `injectedByBoundaryIndex` map by. A mid-
 * paragraph `'line'` start never appears here at all — no injectable host
 * exists (§0), closed. A mid-row `'rowSegment'` start (RECUR-27604 §14/§15)
 * appears here ONLY when the §15.1 sibling-empty guard passes — i.e. no
 * SIBLING `<td>` (other than the row's primary cell, which legitimately
 * continues across the cut) has content crossing the cut line. `element` is
 * then the cut Slate-block child itself (the same element `UnitMeta.element`
 * already points to), `gapPx` is always 0 (mirrors the `'row'` case — no
 * model leftover to add). When the guard fails, the seam stays un-hostable,
 * same terminal bucket as before. Consumed by `usePageBoundaries`'s
 * `applyPageGapStyles`; kept separate from `computePageBoundaries` (a pure
 * Y/gap projection with no element identities) and from
 * `computePageStartUnits` (the untouched export projection).
 */
export interface PageGapTarget {
    element: HTMLElement;
    gapPx: number;
    kind: 'block' | 'row' | 'rowSegment';
    boundaryIndex: number;
}

/**
 * One page-END gap-application target (RECUR-27604 §13.3) — the mirror image
 * of `PageGapTarget`, sitting on the LAST unit of the page ABOVE a boundary
 * instead of the first unit of the page below it. `usePageBoundaries` paints
 * `EDITOR_PAGE_END_SLOT_PX` (bottom margin/padding) on `element` the same way
 * it paints `EDITOR_PAGE_BREAK_SLOT_PX` on a `PageGapTarget`'s element.
 * `boundaryIndex` is the boundary this end-slot belongs to (i.e. the page
 * ABOVE `PageBoundaries.boundaries[boundaryIndex]`) — the SAME index space as
 * `PageGapTarget.boundaryIndex`, so `usePageBoundaries` can key both maps by
 * the same `i`. A `'line'` last unit never appears here (no block-level box
 * at the page's bottom — §13.3). A `'row'`/`'block'` unit that is itself
 * split by an immediately-following `'rowSegment'` of the SAME live `<tr>`
 * (i.e. the boundary's START unit, `nextMeta`, is `'rowSegment'`) is handled
 * by its OWN dedicated branch (RECUR-27604 §15.7/SF-2): when the §15.1
 * sibling-empty guard passes, this target's `kind` is `'rowSegment'` and
 * `element` is `getSlateCellChildren(primaryCell)[nextMeta.cellChildIndex − 1]`
 * — the OUTGOING page's LAST paragraph of that SAME cell, never
 * `unitMeta[startIdx − 1].element` (which, for a 3+-page cell, is the
 * PREVIOUS segment's START child, not this page's own last one). When the
 * guard fails, no target is emitted for that boundary at all (§14.5's
 * original bare terminal state).
 */
export interface PageEndGapTarget {
    element: HTMLElement;
    kind: 'block' | 'row' | 'rowSegment';
    boundaryIndex: number;
}

/**
 * The CSS custom property `applyPageGapStyles` writes the injected gap onto
 * a page-start block with, and this module reads back to NORMALIZE live
 * geometry (see the module doc's coordinate-space note). Self-describing by
 * design — the DOM itself is the only source of truth for "how much gap is
 * currently painted where", so there is no separate mutable state that could
 * ever drift from what's actually rendered.
 */
export const PAGE_GAP_CSS_VAR = '--page-gap';

/**
 * The page-END analogue of `PAGE_GAP_CSS_VAR` (RECUR-27604 §13.3(b)) — a
 * SEPARATE custom property (not reused) because a top slot and an end slot
 * can both be painted on the very SAME element (a one-page-tall doc whose
 * only block is simultaneously the page-1... no — practically: a table whose
 * row group is both a `'row'`-kind page START on one boundary and the LAST
 * unit before the NEXT boundary needs both `margin-top`/`padding-top` AND
 * `margin-bottom`/`padding-bottom` independently addressable, which one
 * shared property could not express.
 */
export const PAGE_END_GAP_CSS_VAR = '--page-end-gap';

/** Reads back the gap (CSS px) currently painted on `el` via `PAGE_GAP_CSS_VAR`, or 0 if none. */
export function getInjectedGapPx(el: HTMLElement): number {
    const px = parseFloat(el.style.getPropertyValue(PAGE_GAP_CSS_VAR));
    return Number.isFinite(px) && px > 0 ? px : 0;
}

/** Reads back the END gap (CSS px) currently painted on `el` via `PAGE_END_GAP_CSS_VAR`, or 0 if none. */
export function getInjectedEndGapPx(el: HTMLElement): number {
    const px = parseFloat(el.style.getPropertyValue(PAGE_END_GAP_CSS_VAR));
    return Number.isFinite(px) && px > 0 ? px : 0;
}

/** `true` for the exact element `TableElement` renders as a Slate table node. */
function isSlateTableElement(el: Element): el is HTMLTableElement {
    return el.tagName === 'TABLE' && el.getAttribute('data-slate-node') === 'element';
}

/**
 * Finds the `<table>` a top-level block either IS or WRAPS. `TableElement`
 * (tableElements.tsx) renders every table inside a plain `.tableScroll` div
 * (RECUR-26175's horizontal-scroll wrapper for wide pastes) — that div, NOT
 * the `<table>` itself, is what sits directly under `plate-editor-content`
 * (the div carries none of Slate's own attributes; the table one level
 * inside it does). Checking the block's own tag first keeps this correct if
 * a future change ever renders the table without that wrapper.
 *
 * Deliberately scoped to a DIRECT child, or a table that's a direct child of
 * ONE direct-child wrapper (the `.tableScroll` shape above) — never an
 * unbounded-depth descendant search. A composite top-level block (column,
 * callout, blockquote — nested tables are open tech debt, see recur-wiki)
 * can CONTAIN a table several levels down without that table being the
 * block's own top-level table; an unscoped `querySelector` would match it
 * anyway and this function's callers would then decompose the WHOLE
 * composite block row-by-row against that unrelated nested table's geometry.
 *
 * Walked explicitly (not a `:scope`-combinator `querySelector`) — jsdom's
 * `:scope` scoping is unreliable on a DETACHED subtree (matches at any depth
 * instead of respecting the `>` combinator), which is exactly how this
 * module's own tests (and the exporter's) build every fixture; explicit
 * child-walking is correct regardless of whether `block` is attached.
 */
export function findTableElement(block: HTMLElement): HTMLTableElement | null {
    if (block.tagName === 'TABLE') return block as unknown as HTMLTableElement;
    for (const child of Array.from(block.children)) {
        if (isSlateTableElement(child)) return child;
        if (child.tagName === 'TABLE') continue; // never descend into a nested table's own cells
        for (const grandchild of Array.from(child.children)) {
            if (isSlateTableElement(grandchild)) return grandchild;
        }
    }
    return null;
}

/**
 * The same top-level-block filter `planPages` uses (exportToPdf.ts:606-608),
 * WIDENED for tables: a `.tableScroll`-wrapped table's `<table
 * data-slate-node="element">` lives ONE level inside `contentEl`'s direct
 * child (see `findTableElement`), so the bare `data-slate-node==='element'`
 * check alone never matches the wrapper — every table in the document would
 * otherwise be invisible here, contributing zero height to the model. A
 * top-level element counts as a block when it EITHER carries
 * `data-slate-node="element"` itself (every other block type, and a table
 * that somehow renders unwrapped) OR wraps one.
 */
export function getTopLevelBlocks(contentEl: HTMLElement): HTMLElement[] {
    return Array.from(contentEl.children).filter((child): child is HTMLElement => {
        if (!(child instanceof HTMLElement)) return false;
        if (child.getAttribute('data-slate-node') === 'element') return true;
        return findTableElement(child) !== null;
    });
}

/**
 * Cumulative `.offsetTop` of `el` relative to `ancestor`, walking the
 * `offsetParent` chain instead of trusting a single `.offsetTop` read. A
 * `<tr>`'s `offsetParent` stops AT its `<table>` — the CSSOM `offsetParent`
 * algorithm special-cases `td`/`th`/`table` as a boundary regardless of
 * position — so `row.offsetTop` alone is relative to the TABLE, not to
 * `plate-editor-content` like every other top-level block's `offsetTop`
 * already is. Summing the chain restores a value in that same shared space.
 * Falls back to 0 extra if the chain never reaches `ancestor` (defensive —
 * shouldn't happen for a row that's actually inside the measured table).
 */
function cumulativeOffsetTop(el: HTMLElement, ancestor: HTMLElement): number {
    let top = 0;
    let node: HTMLElement | null = el;
    while (node && node !== ancestor) {
        top += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
    }
    return top;
}

/**
 * Sum of `getInjectedGapPx` over every `<tr>` of `block`'s own table (0 for a
 * non-table block, or a table with no row currently gapped) — RECUR-27604
 * §8.2's `topShift` accumulator needs this to know how much of `block`'s
 * OWN row gaps must shift every LATER block's offsetTop, without shifting
 * `block`'s own (a row gap is cell padding INSIDE the table, so it can never
 * move the table's own top — only what comes after it).
 */
function totalRowGapPx(block: HTMLElement): number {
    const table = findTableElement(block);
    if (!table) return 0;
    return Array.from(table.rows).reduce((sum, row) => sum + getInjectedGapPx(row), 0);
}

/**
 * Sum of `getInjectedEndGapPx` over every `<tr>` of `block`'s own table
 * (RECUR-27604 §13.3(b)) — the END-gap mirror of `totalRowGapPx`, needed for
 * the SAME reason: a row's own `padding-bottom` end slot shifts every LATER
 * block's offsetTop (it inflates that row's own bottom, which pushes
 * everything after the table down), never the table's own top.
 */
function totalRowEndGapPx(block: HTMLElement): number {
    const table = findTableElement(block);
    if (!table) return 0;
    return Array.from(table.rows).reduce((sum, row) => sum + getInjectedEndGapPx(row), 0);
}

/**
 * Sum of injected top+end cell-paragraph gaps (RECUR-27604 §15.2/§15.3's
 * mid-cell vehicle) over every cell's Slate-block children of ONE row — 0
 * unless a `'rowSegment'` boundary inside THIS row is currently painted (only
 * possible on the export path, at an already-hosted seam). Needed by
 * `buildAtomicUnits` to keep a segmented row's own `groupHeight` gap-free
 * (§15.3 item 2), mirroring `rowGapBefore`/`rowGapUpTo`'s row-level
 * normalization one level up.
 */
function rowCellSegGapPx(row: HTMLTableRowElement): number {
    return Array.from(row.cells).reduce(
        (sum, cell) => sum + getSlateCellChildren(cell).reduce((s, child) => s + getInjectedGapPx(child) + getInjectedEndGapPx(child), 0),
        0,
    );
}

/**
 * `rowCellSegGapPx`, summed over EVERY row of `block`'s own table (RECUR-27604
 * §15.3 item 3) — the mid-cell analogue of `totalRowGapPx`/`totalRowEndGapPx`:
 * a cell-paragraph gap painted inside `block`'s table shifts every LATER
 * block's offsetTop (it inflates the table's, hence the block's, own
 * `offsetHeight`), never `block`'s own top, so it folds into `shiftBefore`
 * exactly like the row-level accumulators already do.
 */
function totalCellSegGapPx(block: HTMLElement): number {
    const table = findTableElement(block);
    if (!table) return 0;
    return Array.from(table.rows).reduce((sum, row) => sum + rowCellSegGapPx(row), 0);
}

// ── Table row-group decomposition (RECUR-27304 rowspan-safe pagination) ─────
//
// A `rowSpan=N` cell drawn in row `i` visually occupies rows `[i, i+N-1]`,
// even though it lives in ONE `<tr>` (row `i`'s own). Treating every `<tr>`
// as an independently-breakable unit (the pre-fix decomposition below) let
// the model choose a break INSIDE that span; the export then physically
// slices the table at that row (`splitOneTableAtRows`, exportToPdfServer.ts),
// stranding the spanning cell in whichever half kept its origin `<tr>` — the
// corruption this section exists to fix. Headless Chromium's own native
// fragmentation already treats a rowspan group as atomic (verified via a
// real render, RECUR-27304): it only ever seams BETWEEN such groups, never
// inside one. `computeRowGroups` makes this model do the same —
// `buildAtomicUnits` below decomposes a table into one atomic unit per GROUP
// instead of per `<tr>`; a table with no `rowSpan` anywhere degenerates to
// one group per row, so this is byte-identical to the old per-`<tr>`
// decomposition for every plain table.

/**
 * One maximal run of a table's `<tr>` indices bound together by any
 * `rowSpan`, both ends inclusive — see this section's own module comment for
 * why a page break may only ever land at a group's own `start`, never inside
 * one.
 */
export interface TableRowGroup {
    /** Index into `table.rows` of this group's first row (inclusive) — the ONLY row index a page-break may ever land on. */
    start: number;
    /** Index into `table.rows` of this group's last row (inclusive) — the furthest row any rowSpan opened within the group still reaches. */
    end: number;
}

/**
 * Groups `rows` (a table's `<tr>`s, or an equivalent `ArrayLike` in tests)
 * into maximal `TableRowGroup` runs — a standard interval-merge: `reach`
 * tracks the furthest row any cell seen so far in the CURRENT group demands
 * (`rowIndex + rowSpan - 1`), and a group closes at row `i` exactly when
 * `i === reach` — nothing still open extends past it. `reach = Math.max(reach, i)`
 * floors `reach` at the row just processed on every iteration: a `<tr>`
 * fully COVERED by an earlier row's rowSpan contributes no `<td>`/`<th>` of
 * its OWN here (the DOM never lists a covered slot as a child of the row it
 * merely passes through), so without the floor a covered row would read a
 * stale `reach` left over from before the group even started. A table with
 * NO `rowSpan` anywhere degenerates to one group PER ROW (every cell's own
 * span is 1, so `reach` always lands on the current row) — byte-identical to
 * treating every `<tr>` as its own atomic unit, i.e. this codebase's pre-fix
 * behaviour for every plain table.
 */
export function computeRowGroups(rows: ArrayLike<HTMLTableRowElement>): TableRowGroup[] {
    const groups: TableRowGroup[] = [];
    let groupStart = 0;
    let reach = -1;
    for (let i = 0; i < rows.length; i++) {
        Array.from(rows[i]?.cells ?? []).forEach((cell) => {
            reach = Math.max(reach, i + Math.max(cell.rowSpan || 1, 1) - 1);
        });
        reach = Math.max(reach, i);
        if (i === reach) {
            groups.push({ start: groupStart, end: i });
            groupStart = i + 1;
            reach = -1;
        }
    }
    return groups;
}

// ── Line decomposition (RECUR-27304 Delta v5) ────────────────────────────────
//
// Parallel to the table-row decomposition above: a long TEXT paragraph is
// split into one flat unit per rendered LINE instead of being pushed whole,
// so it flows across a page break like Word instead of leaving a page-sized
// gap. `measureBlockLines` is exported so `exportToPdfServer.ts`'s clone-split
// step can re-measure the SAME live block in the SAME (viewport) space its
// own caret-rect comparisons use, rather than reusing this module's
// NORMALIZED offsetTop values (which would be off by whatever gap is
// currently painted on the block — see this module's own coordinate-space
// note at the top of the file).

/** One measured visual line, in VIEWPORT (CSS px) coordinates. */
export interface MeasuredLine {
    top: number;
    bottom: number;
}

const LINE_RECT_TOP_TOLERANCE_PX = 1;

/**
 * Groups a Range's per-inline-run rects into visual LINES —
 * `Range.getClientRects()` returns one rect per inline run (a bold word, a
 * link, a plain text run, …), several of which share the SAME visual line.
 * An exact-`top` tolerance alone isn't enough: a line with MIXED inline
 * metrics (a bold word, a superscript, a link rendered in a different
 * font-size) yields rects whose `top`s genuinely differ by a few px even
 * though they're all on the same visual line — matched by exact tolerance
 * alone, those over-count as separate lines. A rect instead joins an
 * existing line when EITHER its `top` is within the small tolerance
 * (sub-pixel rounding between adjacent runs at the SAME size) OR its own
 * vertical midpoint falls inside that line's current `[top, bottom]` band
 * (catches a same-line run whose top/bottom sit at a different height
 * entirely). On join, the band widens to min(top)/max(bottom) across every
 * rect that landed in it — this can also pull a LATER rect's `top` closer to
 * an earlier one's, so band-overlap is re-checked against the (possibly
 * already-widened) candidate, not just the first rect that started it. A
 * degenerate (zero-area) rect — an empty text node, a collapsed range at a
 * boundary — is skipped. Sorted top-to-bottom (defensive: this editor's
 * plain LTR text flow already returns rects in that order, but
 * grouping-then-sorting doesn't depend on it).
 */
function groupRectsIntoLines(rects: ArrayLike<DOMRect>): MeasuredLine[] {
    const lines: MeasuredLine[] = [];
    Array.from(rects).forEach((rect) => {
        if (rect.width <= 0 && rect.height <= 0) return;
        const midY = (rect.top + rect.bottom) / 2;
        const line = lines.find(
            (candidate) =>
                Math.abs(candidate.top - rect.top) <= LINE_RECT_TOP_TOLERANCE_PX || (midY >= candidate.top && midY <= candidate.bottom),
        );
        if (line) {
            line.top = Math.min(line.top, rect.top);
            line.bottom = Math.max(line.bottom, rect.bottom);
        } else {
            lines.push({ top: rect.top, bottom: rect.bottom });
        }
    });
    return lines.sort((a, b) => a.top - b.top);
}

/**
 * Measures `block`'s rendered visual lines in VIEWPORT (CSS px) coordinates.
 * `[]` for an empty/whitespace-only block (no rects) or when Range
 * measurement is unavailable (defensive — mirrors the ResizeObserver guard
 * elsewhere in this codebase).
 */
export function measureBlockLines(block: HTMLElement): MeasuredLine[] {
    if (typeof document === 'undefined' || typeof document.createRange !== 'function') return [];
    const range = document.createRange();
    range.selectNodeContents(block);
    const rects = range.getClientRects ? range.getClientRects() : ([] as unknown as DOMRectList);
    return groupRectsIntoLines(rects);
}

/**
 * `measureBlockLines`, converted into the SAME (LIVE, gap-included)
 * offsetTop-space `buildAtomicUnits` already reasons in — mirrors
 * `cumulativeOffsetTop`'s coordinate-space conversion for table rows, via
 * `getBoundingClientRect` instead of the offsetParent chain (a plain
 * paragraph has no offsetParent quirk to work around, unlike a `<tr>`). NOT
 * yet gap-normalized — the caller applies the same per-block cumulative-gap
 * subtraction it already uses for the whole-block case.
 */
function measureLineOffsets(block: HTMLElement): MeasuredLine[] {
    const lines = measureBlockLines(block);
    if (lines.length === 0) return [];
    const blockViewportTop = block.getBoundingClientRect().top;
    return lines.map((line) => ({
        top: line.top - blockViewportTop + block.offsetTop,
        bottom: line.bottom - blockViewportTop + block.offsetTop,
    }));
}

// A cheap, CONSERVATIVE pre-filter so a document with many short (typically
// single-line) paragraphs doesn't force a Range/getClientRects measurement on
// every one of them on every recompute pass (this hook re-measures on every
// ResizeObserver-flagged reflow — see usePageBoundaries.ts). Deliberately well
// under the shortest plausible height of a genuinely-decomposable (>= 4-line)
// paragraph at this editor's smallest realistic line-height, so this can only
// ever skip a block that's obviously 1 line — never a false negative; a block
// that's merely 2-3 lines (also not decomposed — see LINE_ORPHAN_WIDOW_MIN_LINES)
// gets measured and correctly rejected either way, same outcome.
const MIN_HEIGHT_FOR_LINE_MEASUREMENT_PX = 32;

/**
 * Whether `block` is even a CANDIDATE for line decomposition — checked
 * BEFORE measurement, so ineligible blocks never pay for a Range/
 * getClientRects call at all.
 *
 * RECUR-27304 (font/DIV-parity investigation): WIDENED from a `tagName ===
 * 'P'` whitelist. Live inspection showed the One Pager editor renders EVERY
 * top-level block — including plain paragraphs — as a `DIV`
 * (`data-slate-node="element"`), never a literal `<p>`, so the old whitelist
 * silently never fired there at all (Delta v5 was dead code for the One
 * Pager). A tag whitelist is exactly the kind of thing that breaks again the
 * next time a block's underlying tag changes, so eligibility is now
 * STRUCTURAL (content-based) instead: a top-level `P` or `DIV` block that —
 *   • is not a table: `findTableElement` returns null (already decomposed
 *     into ROW units above when it has >=2 rows) AND it contains no nested
 *     `<table>` either (defensive — e.g. pasted HTML without the Slate
 *     table's own `data-slate-node` hook);
 *   • carries no media/structural descendant (`<img>`, `<figure>`, `<hr>`,
 *     `<iframe>`, `<video>`) — none of these flow as stacked reading lines,
 *     so `Range.getClientRects()` on one would return rects the grouping
 *     above was never built to interpret;
 *   • has non-empty visible text (an empty caret-landing `<p>`/`<div>` has
 *     nothing to measure lines from).
 * This deliberately still covers a list-item — `BlockList.tsx` wraps a list
 * block's OWN text in `<ul>/<li>`, but the block itself stays a top-level
 * `P`/`DIV` — the same as before, just no longer by accident of `<p>` being
 * the only tag ever whitelisted.
 *
 * The safeguards BELOW this check are what make trusting a wider structural
 * predicate safe rather than needing an exhaustive tag blacklist (headings,
 * blockquote, callout, code-block, TOC, column-group, …): a short block
 * (heading, one-line callout) never clears `MIN_HEIGHT_FOR_LINE_MEASUREMENT_PX`
 * or `LINE_ORPHAN_WIDOW_MIN_LINES` below, and a structured block whose
 * `Range.getClientRects()` doesn't measure as expected (or returns nothing —
 * `measureBlockLines`'s empty-result case) falls back to the pre-existing
 * whole-block push, exactly as every non-candidate already does today.
 *
 * Shared by BOTH the One Pager and the Reports editor (`PlateDocumentDrawer`
 * renders the SAME `PlateEditor` — no per-surface markup fork), so this
 * widening applies identically to both; nothing here is One-Pager-specific.
 */
function isLineDecomposableBlock(block: HTMLElement): boolean {
    if (block.tagName !== 'P' && block.tagName !== 'DIV') return false;
    if (findTableElement(block) !== null) return false;
    if (block.querySelector('table, img, figure, hr, iframe, video')) return false;
    return (block.textContent || '').trim().length > 0;
}

// ── In-row split for oversized table rows (RECUR-27604 §14) ─────────────────
//
// A Word import can cram a whole section into ONE table cell, producing a
// single row nearly a page tall. Atomic, that row either PUSHes whole
// (orphaning a near-blank page before it) or EXTENDs (planning a page taller
// than the physical paper, desyncing the exported PDF from the editor). A
// single-row group taller than `OVERSIZED_ROW_SPLIT_RATIO` of the page
// decomposes into per-cell-paragraph SEGMENT units at its PRIMARY cell's
// Slate-block boundaries, so the fit-loop can place an ordinary cut INSIDE
// the row. Editor==PDF page COUNT comes from CAPACITY (the segment
// decomposition), not from any display chrome — but RECUR-27604 §15
// conditionally HOSTS a `'rowSegment'` seam with the SAME full chrome as any
// other seam (top slot + logo, page-end padding) whenever the §15.1
// sibling-empty guard passes (no OTHER cell's content crosses the cut); when
// it fails, the seam stays UN-HOSTABLE exactly as §14.0 originally decided —
// see `PageGapTarget`'s own doc and `siblingContentCrossesRowSegmentCut`.

/**
 * A cell's Slate BLOCK children (`data-slate-node="element"`) — the only
 * children an in-row cut may reference. Editor chrome inside a `<td>` (the
 * column/row drag handles `TableElement` renders, absolutely positioned and
 * as tall as the row itself) is excluded on BOTH sides of the pipeline: here
 * when planning cuts, and in the exporter when applying them — the export
 * clone has its chrome stripped (`stripInteractiveChrome`) before the row is
 * cut, so a raw child index would desync between the live cell and the
 * cloned one.
 */
export function getSlateCellChildren(cell: HTMLTableCellElement): HTMLElement[] {
    return Array.from(cell.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement && child.getAttribute('data-slate-node') === 'element',
    );
}

/**
 * One planned in-row cut candidate for an oversized single-row group (§14) —
 * `topWithinRow` is RAW (live, gap-included — includes the row's OWN
 * injected cell-padding slot, if this same row is also a `'row'`-kind page
 * start, §8.1). `buildAtomicUnits` normalizes it (subtracts `rowOwnGapPx`)
 * before feeding it to `geometry`, mirroring how it already normalizes every
 * other row-gap-affected value (§8.2) — see this file's coordinate-space
 * module doc. Segment 0 (the row's own, un-split start) is never represented
 * here — only cuts 1..n are, exactly what `measureRowSegments` measures.
 *
 * `cellGapBefore` (RECUR-27604 §15.3, the mid-cell chrome crux) — the SUM of
 * every injected top+end cell-paragraph gap (§15.2's `.pageCellSegGapBefore`/
 * `.pageCellSegEndGapAfter` vehicle) on the primary cell's Slate children
 * STRICTLY BEFORE this segment's `childIndex` (exclusive — a child's own top
 * padding never shifts its OWN top, only a PRECEDING child's top or bottom
 * padding does). 0 unless a mid-cell gap was already painted on an EARLIER
 * child (only possible on the export path, at an already-hosted `'rowSegment'`
 * boundary, mirroring `rowOwnGapPx`'s own "only nonzero on export" note).
 * `buildAtomicUnits` subtracts it — ON TOP of `rowOwnGapPx` — from this
 * segment's own normalized top, exactly mirroring how `rowGapBefore`/
 * `rowGapUpTo` already normalize a row's own painted gap one level up.
 */
interface RowSegment {
    topWithinRow: number;
    cellIndex: number;
    childIndex: number;
    startChild: HTMLElement;
    cellGapBefore: number;
}

/**
 * Plans the in-row cut positions for an oversized single-row group (§14):
 * candidate cuts are the tops of the PRIMARY cell's Slate block children —
 * cell-paragraph boundaries, not rendered lines — filtered to
 * strictly-increasing NORMALIZED positions strictly inside the row (`0 <
 * top < groupHeight`, both gap-free — `groupHeight` is already gap-free per
 * §8.2, so each candidate's raw top is normalized by `rowOwnGapPx` before the
 * comparison, mirroring the same subtraction `buildAtomicUnits` re-applies to
 * build `geometry`). The primary cell is the one with the MOST Slate block
 * children (content height ties on every cell: a `<td>` stretches to the
 * row's height, and the label cell also hosts the row-tall drag-handle
 * chrome), broken by taller summed block content. Returns `null` when the
 * row can't be segmented (no cells, the primary cell has fewer than 2 block
 * children, or no candidate cut survives the strictly-inside-the-row filter)
 * — the caller then keeps the row atomic, byte-identical to today.
 *
 * Does NOT compute segment heights — the caller (`buildAtomicUnits`) owns
 * the normalized/live coordinate split (this file's own module doc) and
 * derives each segment's `offsetHeight` from consecutive normalized tops.
 */
function measureRowSegments(
    row: HTMLTableRowElement,
    table: HTMLTableElement,
    rowTopWithinTable: number,
    groupHeight: number,
    rowOwnGapPx: number,
): RowSegment[] | null {
    const cells = Array.from(row.cells);
    if (cells.length === 0) return null;
    const slateChildrenByCell = cells.map(getSlateCellChildren);
    const contentHeightOf = (children: HTMLElement[]) => children.reduce((sum, child) => sum + child.offsetHeight, 0);
    let primaryCellIndex = 0;
    slateChildrenByCell.forEach((children, i) => {
        const best = slateChildrenByCell[primaryCellIndex];
        if (children.length > best.length || (children.length === best.length && contentHeightOf(children) > contentHeightOf(best))) {
            primaryCellIndex = i;
        }
    });
    const children = slateChildrenByCell[primaryCellIndex];
    if (children.length < 2) return null;

    const segments: RowSegment[] = [];
    let previousNormalizedTop = 0; // segment 0's own (implicit, un-returned) normalized top
    // RECUR-27604 §15.3 — running Σ of every EARLIER child's own injected
    // top+end cell-paragraph gap, used (but not yet updated with the CURRENT
    // child's own gap) at the point each candidate cut is evaluated — see
    // `RowSegment.cellGapBefore`'s own doc comment.
    let cellGapBefore = 0;
    children.forEach((child, childIndex) => {
        if (childIndex > 0) {
            const rawTopWithinRow = cumulativeOffsetTop(child, table) - rowTopWithinTable;
            const normalizedTop = rawTopWithinRow - rowOwnGapPx - cellGapBefore;
            if (normalizedTop > previousNormalizedTop && normalizedTop < groupHeight) {
                segments.push({ topWithinRow: rawTopWithinRow, cellIndex: primaryCellIndex, childIndex, startChild: child, cellGapBefore });
                previousNormalizedTop = normalizedTop;
            }
        }
        cellGapBefore += getInjectedGapPx(child) + getInjectedEndGapPx(child);
    });
    return segments.length > 0 ? segments : null;
}

/**
 * Per-block unit-building result: `geometry` is the FLAT atomic-unit list fed
 * to `pageBreakModel`, in NORMALIZED (gap-free) coordinates — one entry per
 * whole block, per rowspan-bound row GROUP for a decomposed table
 * (RECUR-27304 rowspan-safe pagination — ordinarily one `<tr>` each, see
 * `computeRowGroups`), or per rendered LINE for a decomposed paragraph
 * (RECUR-27304 Delta v5); `liveOffsetTop` is the SAME flat list's CURRENT
 * on-screen offsetTop (gap included, whatever is actually painted right
 * now), used only to project a model decision back onto the live DOM for the
 * overlay. `rowCounts`/`lineCounts`/`unitStart` are parallel to the ORIGINAL
 * `blocks` array and are exactly what `reindexGroupsForRowsAndLines` needs to
 * re-map `buildPaginationGroups`' block-level groups onto `geometry`'s
 * indices — `rowCounts` now counts row GROUPS, not literal `<tr>`s, but
 * `reindexGroupsForRowsAndLines` needs no change for that: it only ever
 * reasons about a COUNT of flat units, never what a unit represents.
 * `rowGroups` is parallel to `blocks` too (`[]` for a non-table/
 * non-decomposed block) — `buildUnitMeta` needs the actual group boundaries
 * to resolve each row-kind unit's live `<tr>` and `rowIndex`.
 *
 * RECUR-27604 §8.2 (row-gap normalization — `geometry` only): a row-kind
 * unit's `geometry` entry is normalized by that row GROUP's own cumulative
 * row gap (`rowGapBefore` for its top, exclusive of its own gap;
 * `rowGapUpTo` for its bottom, inclusive — a row's own padding never shifts
 * its own top but DOES inflate its own bottom), on top of the enclosing
 * block's `topShift`. `liveOffsetTop`, by contrast, is pushed from the
 * UN-NORMALIZED `liveGroupTopWithinTable` (see `buildAtomicUnits`'s table
 * branch) — never the normalized value — because it must keep meaning
 * exactly what its own name says: "the SAME flat list's CURRENT on-screen
 * offsetTop, gap included, whatever is actually painted right now". Mixing
 * the two coordinate spaces in that one field is exactly the class of bug
 * this file's own module doc exists to prevent.
 *
 * `applyRowOffsetCorrection` (RECUR-27604 editor-band flag) decouples §8.3's
 * table-offset correction from `reservedPageTopPx` itself: the correction
 * only ever affects `liveOffsetTop` (an overlay-only projection — see
 * `PageStartCore`'s own doc), so it is safe to turn off independently of the
 * model's CAPACITY reservation, which must stay driven by `reservedPageTopPx`
 * alone so editor seams keep matching the PDF regardless of the editor's own
 * logo/slot preview flags (RECUR-27604 §11, rev 6: `usePageBoundaries.ts`
 * passes its own `slotActive` here — `showPageGaps` alone, on wherever a mask
 * anchors — not the logo-gated `bandActive`).
 * Defaults to `reservedPageTopPx > 0` — today's exact behavior — so every
 * existing caller (the export path's `computePageStartUnits`, and any test
 * that doesn't pass a 4th argument) is byte-for-byte unaffected.
 *
 * `pageHeightPx` (RECUR-27604 §14, in-row split) — the SAME capacity number
 * the fit-loop uses (`computePageStartIndices`'s one shared expression, with
 * the logo reclaim already folded in) — is the oversized-row decomposition
 * gate's basis (`OVERSIZED_ROW_SPLIT_RATIO * pageHeightPx`), so a row is
 * never segmented against a DIFFERENT number than the one that decides
 * whether it fits.
 */
function buildAtomicUnits(
    blocks: HTMLElement[],
    reservedPageTopPx: number,
    pageHeightPx: number,
    applyRowOffsetCorrection: boolean = reservedPageTopPx > 0,
): {
    geometry: BlockGeometry[];
    liveOffsetTop: number[];
    rowCounts: number[];
    rowGroups: TableRowGroup[][];
    /**
     * Parallel to `rowGroups` (one entry per group in `rowGroups[blockIdx]`,
     * `[]` for a non-table block) — the oversized-row segment plan for that
     * group, or `null` when the group wasn't segmented (§14.1E). `[]` for a
     * non-table block, `null` per-group when that group didn't qualify.
     * `buildUnitMeta` (Option B — kept separate from this function) needs
     * this to emit each segment's `'rowSegment'` metadata without
     * re-measuring (no drift — the plan is measured ONCE, here, and consumed).
     */
    segmentPlansByBlock: (RowSegment[] | null)[][];
    lineCounts: number[];
    unitStart: number[];
} {
    const geometry: BlockGeometry[] = [];
    const liveOffsetTop: number[] = [];
    const rowCounts: number[] = [];
    const rowGroups: TableRowGroup[][] = [];
    const segmentPlansByBlock: (RowSegment[] | null)[][] = [];
    const lineCounts: number[] = [];
    const unitStart: number[] = [];

    // RECUR-27604 §8.2 — two accumulators, exact (replaces the old
    // single-accumulator `gapAboveAndIncluding`, which fed a block's OWN row
    // gaps into every offsetTop including that same block's own top — wrong
    // the moment a row can be gapped, since a row gap is cell padding INSIDE
    // the table and can never move the table's own top). `topShift[i]` is
    // what must be subtracted from block i's LIVE offsetTop to recover its
    // gap-free position: `ownGap` (the block's own margin-top, INCLUSIVE —
    // it shifts block i's OWN top too, not just siblings below it) plus
    // whatever shifted in from EARLIER blocks; a row gap painted INSIDE
    // block i's own table (`totalRowGapPx`) shifts only what comes AFTER
    // block i (D1's fix). Byte-identical to the old single accumulator
    // whenever no row gap exists anywhere (`totalRowGapPx() === 0`).
    // RECUR-27604 §13.3(b) extends this with END gaps: a block's OWN
    // `margin-bottom` (or a row's own `padding-bottom`) never shifts that
    // SAME element's own top — it leaves `offsetHeight` of everything ABOVE
    // it untouched and only pushes what comes AFTER it — so both
    // `totalRowEndGapPx(block)` (this block's own table's row end-gaps) and
    // `getInjectedEndGapPx(block)` (the block's own end-gap) fold into
    // `shiftBefore` (what later blocks see), never into `shift` (this
    // block's own topShift).
    let shiftBefore = 0;
    const topShift = blocks.map((block) => {
        const ownGap = getInjectedGapPx(block);
        const shift = shiftBefore + ownGap;
        // RECUR-27604 §15.3 item 3 — a cell-paragraph gap (§15.2's mid-cell
        // vehicle) inside this block's OWN table shifts every LATER block
        // down, never this block's own top, exactly like the row-level
        // accumulators beside it.
        shiftBefore = shift + totalRowGapPx(block) + totalRowEndGapPx(block) + getInjectedEndGapPx(block) + totalCellSegGapPx(block);
        return shift;
    });

    blocks.forEach((block, blockIdx) => {
        unitStart.push(geometry.length);
        const liveTop = block.offsetTop;
        // NORMALIZED: what this block's offsetTop would be with NO gap ever
        // applied — the only space `pageBreakModel` is allowed to see, so
        // re-running the pipeline against already-gapped geometry reproduces
        // the SAME decision instead of oscillating (RECUR-27304
        // measurement-feedback-loop fix).
        const normalizedTop = liveTop - topShift[blockIdx];
        const table = findTableElement(block);
        // table.rows (not a recursive querySelectorAll) matches only THIS
        // table's own rows — never a nested table's, if one is ever nested
        // inside a cell — and is the same API exportHelpers.ts's
        // measureRenderedColumnWidths already uses for table rows.
        const rows = table ? Array.from(table.rows) : [];
        // RECUR-27304 rowspan-safe pagination: decompose by rowspan-bound
        // GROUP (computeRowGroups), not by literal `<tr>` — see this file's
        // "Table row-group decomposition" section above. A table with no
        // rowSpan anywhere yields one group per row, so `tableRowGroups`
        // below is byte-identical to `rows` itself for every plain table.
        const tableRowGroups = table && rows.length >= 2 ? computeRowGroups(table.rows) : [];
        if (tableRowGroups.length >= 2) {
            rowGroups.push(tableRowGroups);
            lineCounts.push(0);
            const tableEl = table as HTMLTableElement;

            // RECUR-27604 §8.2 — per-row cumulative row gap: `rowGapBefore`
            // (exclusive, for a group's TOP — a row's own padding never
            // shifts its own top) and `rowGapUpTo` (inclusive, for a group's
            // BOTTOM — the group's own row gap DOES inflate
            // `lastRow.offsetHeight`, D2's fix). All 0 when no row in this
            // table is currently gapped, so `geometry` below is
            // byte-identical to today in that case.
            // RECUR-27604 §13.3(b): a row's own END gap (`padding-bottom`)
            // inflates that SAME row's own bottom (like its own top gap
            // inflates its own top-normalized... no — a row's own TOP gap
            // never shifts its own top either; both gaps only ever shift
            // what's BELOW/at-and-including this row's own BOTTOM). One
            // combined cumulative therefore covers both: `rowGapBefore[i]`
            // (exclusive of row i's own gaps, for its TOP) and
            // `rowGapUpTo[i]` (inclusive, for its BOTTOM) each fold in BOTH
            // the top gap and the end gap of every row up to the relevant
            // point — exactly mirroring how `topShift`/`shiftBefore` above
            // combine a block's own top/end gaps.
            let rowGapCumulative = 0;
            const rowGapBefore: number[] = [];
            const rowGapUpTo: number[] = [];
            rows.forEach((row) => {
                rowGapBefore.push(rowGapCumulative);
                rowGapCumulative += getInjectedGapPx(row) + getInjectedEndGapPx(row);
                rowGapUpTo.push(rowGapCumulative);
            });

            // RECUR-27604 §8.3 — overlay-only correction for the table's OWN
            // offset within its top-level block: `cumulativeOffsetTop` stops
            // AT `tableEl`, omitting the table's own `margin: 16px 0` inside
            // its `.tableScroll` wrapper, so every row-kind boundary Y is
            // ~16px too high without it. GATED on `applyRowOffsetCorrection`
            // (orchestrator decision; decoupled from `reservedPageTopPx` for
            // the RECUR-27604 editor-band flag — see this function's own doc
            // comment): un-gated, this would silently move every row-kind
            // seam + "Page N" chip on an ordinary NON-logo table document — a
            // visual change this ticket did not ask for, and one the
            // pre-existing `pageStartUnits.test.ts` fixtures can't even see
            // (`makeTableBlock` gives the wrapper and the table the same
            // `offsetTop` and mocks no `offsetParent`, so BOTH branches
            // evaluate to 0 either way). `geometry` (the model) is
            // deliberately left uncorrected in both cases — fixing the
            // model's own 16px under-count would repaginate every table
            // document and belongs in a separate ticket.
            const tableOffsetWithinBlock =
                applyRowOffsetCorrection && tableEl !== block
                    ? tableEl.offsetParent === block
                        ? tableEl.offsetTop
                        : tableEl.offsetTop - liveTop
                    : 0;

            const segmentPlans: (RowSegment[] | null)[] = [];
            let tableUnitCount = 0;

            tableRowGroups.forEach((rowGroup) => {
                const firstRow = rows[rowGroup.start];
                const lastRow = rows[rowGroup.end];
                // LIVE (un-normalized) — what `liveOffsetTop` must keep
                // using; it means "the SAME flat list's CURRENT on-screen
                // offsetTop, gap included" (see this function's own doc
                // comment) — mixing it with the normalized value below would
                // be exactly the coordinate-space bug this file exists to
                // prevent (N-4).
                const liveGroupTopWithinTable = cumulativeOffsetTop(firstRow, tableEl);
                // NORMALIZED (gap-free) — fed to `geometry` (the model)
                // ONLY. Height spans the FULL group (last row's bottom minus
                // first row's top), not just the first row's own height, so
                // a tall rowspan cell's height is attributed to the whole
                // GROUP instead of silently truncated to its origin `<tr>`
                // alone (RECUR-27304 rowspan-safe pagination).
                const groupTopWithinTable = liveGroupTopWithinTable - rowGapBefore[rowGroup.start];
                const groupBottomWithinTable = cumulativeOffsetTop(lastRow, tableEl) + lastRow.offsetHeight - rowGapUpTo[rowGroup.end];
                // RECUR-27604 §15.3 item 2 — a cell-paragraph gap (§15.2)
                // inflates `lastRow.offsetHeight` above (it's cell padding
                // INSIDE that row), so subtract it back out here too, keeping
                // the OVERSIZED_ROW_SPLIT_RATIO gate and the last segment's
                // height gap-free — mirrors `rowGapUpTo`'s own row-gap
                // subtraction one level up. 0 unless `firstRow` was already
                // segmented AND painted (export path only).
                const groupHeight = groupBottomWithinTable - groupTopWithinTable - rowCellSegGapPx(firstRow);

                // RECUR-27604 §14 — oversized SINGLE-ROW group decomposition:
                // only a group that is exactly one row (a rowspan group is
                // never segmented — §14.1H residual (i), out of scope, same
                // paragraph-level granularity as line decomposition) AND
                // taller than the ratio gets a segment plan. `rowOwnGapPx` is
                // this row's OWN injected cell-padding slot (§8.1) — nonzero
                // ONLY when this SAME row is also a `'row'`-kind page start
                // (segIdx-0 carries the painted gap) on the EXPORT path (the
                // hook always measures gap-free — §14.1E); 0 for SEDNA (the
                // giant row is mid-page, not a page-start).
                const rowOwnGapPx = getInjectedGapPx(firstRow);
                const segments =
                    rowGroup.start === rowGroup.end && groupHeight > OVERSIZED_ROW_SPLIT_RATIO * pageHeightPx
                        ? measureRowSegments(firstRow, tableEl, liveGroupTopWithinTable, groupHeight, rowOwnGapPx)
                        : null;
                segmentPlans.push(segments);

                if (!segments) {
                    // Unchanged from the pre-§14 behavior: one flat unit
                    // spanning the whole group.
                    geometry.push({
                        offsetTop: normalizedTop + groupTopWithinTable,
                        offsetHeight: groupHeight,
                    });
                    liveOffsetTop.push(liveTop + tableOffsetWithinBlock + liveGroupTopWithinTable);
                    tableUnitCount++;
                    return;
                }

                // Segmented: partition the group's own [0, groupHeight) span
                // into `segments.length + 1` CONTIGUOUS, non-overlapping flat
                // units — segment 0 (the group's own un-split start, implicit
                // top 0 — a row's own gap never shifts its own top, §8.1) plus
                // one per planned cut, each stopping exactly where the next
                // one starts (or at `groupHeight` for the last). `unitMeta`
                // (`buildUnitMeta`) reads `segments` in this SAME order to
                // emit the matching kind/element per flat index — see its own
                // doc comment for why the two can never drift apart.
                // RECUR-27604 §15.3 item 4 — also subtract each segment's own
                // `cellGapBefore` (Σ of every EARLIER child's own injected
                // cell-paragraph gap) so a segment's emitted geometry stays
                // gap-free even on the export path at an already-hosted
                // 'rowSegment' boundary. 0 for every unpainted document.
                const normTops = [0, ...segments.map((segment) => segment.topWithinRow - rowOwnGapPx - segment.cellGapBefore)];
                const rawTops = [0, ...segments.map((segment) => segment.topWithinRow)];
                normTops.forEach((normTop, i) => {
                    const nextNormTop = i + 1 < normTops.length ? normTops[i + 1] : groupHeight;
                    geometry.push({
                        offsetTop: normalizedTop + groupTopWithinTable + normTop,
                        offsetHeight: nextNormTop - normTop,
                    });
                    // LIVE (un-normalized, RAW) — mirrors N-4: "the SAME flat
                    // list's CURRENT on-screen offsetTop, gap included".
                    liveOffsetTop.push(liveTop + tableOffsetWithinBlock + liveGroupTopWithinTable + rawTops[i]);
                    tableUnitCount++;
                });
            });
            rowCounts.push(tableUnitCount);
            segmentPlansByBlock.push(segmentPlans);
            return;
        }
        rowCounts.push(0);
        rowGroups.push([]);
        // RECUR-27604 §14 residual (N-3): a table whose group count is 0 or 1
        // — including a genuine ONE-ROW table (no rowSpan, exactly one `<tr>`
        // total) — never enters the `tableRowGroups.length >= 2` branch above
        // at all, so it can never be row-segmented even when oversized: it
        // falls through to the whole-block push below (line-decomposition is
        // rejected for a table block by `isLineDecomposableBlock`), exactly
        // like today. Known, out-of-scope residual — not a bug this ticket
        // fixes (§14.1H/§14.8).
        segmentPlansByBlock.push([]);

        // RECUR-27304 Delta v5: attempt line decomposition for an eligible,
        // tall-enough paragraph before falling back to a single whole-block
        // unit — mirrors the table branch above, one flat unit per rendered
        // LINE instead of per `<tr>`. A line-decomposable block never
        // contains a table (`isLineDecomposableBlock` rejects one), so
        // `topShift` alone (identical to today) is the complete
        // normalization here — no row-gap accounting applies.
        const liveLines =
            isLineDecomposableBlock(block) && block.offsetHeight > MIN_HEIGHT_FOR_LINE_MEASUREMENT_PX ? measureLineOffsets(block) : [];
        if (liveLines.length >= LINE_ORPHAN_WIDOW_MIN_LINES) {
            lineCounts.push(liveLines.length);
            liveLines.forEach((line) => {
                geometry.push({ offsetTop: line.top - topShift[blockIdx], offsetHeight: line.bottom - line.top });
                liveOffsetTop.push(line.top);
            });
            return;
        }

        // Not decomposable either way (no table; wrong block type, too
        // short, or fewer than LINE_ORPHAN_WIDOW_MIN_LINES lines) — one
        // whole-block unit, unchanged from today except for the §13.3(b)
        // subtraction below.
        lineCounts.push(0);
        // RECUR-27604 §13.3(b): a whole-block unit's own page-END host
        // (`.pageEndGapAfter`, `border-bottom` — NOT `margin-bottom`, which
        // margin-collapsing would silently swallow against a following
        // sibling's own margin-top, verified in a real browser) inflates
        // `block.offsetHeight` the same way a row's own cell-padding gap
        // inflates `lastRow.offsetHeight` (§8.2/D2) — subtract it back out
        // so `geometry` stays gap-free. 0 for every block that never carries
        // the class (the overwhelming majority), so byte-identical to today
        // whenever no end gap is painted.
        geometry.push({ offsetTop: normalizedTop, offsetHeight: block.offsetHeight - getInjectedEndGapPx(block) });
        liveOffsetTop.push(liveTop);
    });

    return { geometry, liveOffsetTop, rowCounts, rowGroups, segmentPlansByBlock, lineCounts, unitStart };
}

/** Parallel to `geometry` — the live element/kind/table/paragraph metadata for one flat unit. */
interface UnitMeta {
    element: HTMLElement;
    kind: 'block' | 'row' | 'line' | 'rowSegment';
    tableBlock?: HTMLElement;
    rowIndex?: number;
    textBlock?: HTMLElement;
    lineIndex?: number;
    /** Position in `getTopLevelBlocks(contentEl)` — set only for `kind === 'block'`. */
    blockIndex?: number;
    /** `'rowSegment'` only — see `PageStartUnit`'s own doc. */
    cellIndex?: number;
    /** `'rowSegment'` only — see `PageStartUnit`'s own doc. */
    cellChildIndex?: number;
}

/**
 * Builds `unitMeta` in lockstep with `buildAtomicUnits`' `geometry` (same
 * block iteration, same row-group/segment/line expansion) so `unitMeta[i]`
 * always describes the same flat unit as `geometry[i]`. Row-GROUP 0 of a
 * decomposed table (RECUR-27304 rowspan-safe pagination — ordinarily just
 * row 0, see `computeRowGroups`) — and, likewise, LINE 0 of a decomposed
 * paragraph (RECUR-27304 Delta v5) — is a `'block'`-kind unit whose `element`
 * is the table's/paragraph's own top-level block — `reindexGroupsForRowsAndLines`
 * never lets row/line 0 start its OWN group (it's always folded into the
 * preceding sticky group, or stands alone as the merged first group when
 * there's no preceding heading), so if THIS flat index is ever chosen as a
 * page-start it means "the whole table/paragraph (or its heading) begins the
 * page" — nothing to split, matching `'block'` kind. Every later row GROUP is
 * independently breakable (`'row'` kind, `rowIndex` set to the group's own
 * FIRST `<tr>` index — the only row a page-break may ever land on, per
 * `TableRowGroup`); every later line likewise (`'line'` kind, `lineIndex` >= 1
 * — though by construction of the orphan/widow grouping, `lineIndex` 1
 * specifically can never actually be SELECTED as a page-start; see
 * `reindexGroupsForRowsAndLines`).
 *
 * RECUR-27604 §14 (Option B): iterates `rowGroups[blockIdx]` (the actual
 * group array — `rowCounts[blockIdx]` is now a FLAT UNIT count, no longer a
 * usable loop bound, since a segmented group contributes more than one flat
 * unit) and, for each group with a non-`null` `segmentPlansByBlock[blockIdx][g]`,
 * emits one `'rowSegment'` unit per planned cut AFTER that group's own
 * `'block'`/`'row'`-kind unit — reading `cellIndex`/`cellChildIndex`/`element`
 * straight off the plan `buildAtomicUnits` already measured once, so this can
 * never drift from `geometry`'s own segment loop (both walk `rowGroups[blockIdx]`
 * then that group's segment plan, in the same order).
 */
function buildUnitMeta(
    blocks: HTMLElement[],
    rowCounts: number[],
    rowGroups: TableRowGroup[][],
    segmentPlansByBlock: (RowSegment[] | null)[][],
    lineCounts: number[],
): UnitMeta[] {
    const unitMeta: UnitMeta[] = [];
    blocks.forEach((block, blockIdx) => {
        const flatUnitCount = rowCounts[blockIdx] ?? 0;
        if (flatUnitCount >= 2) {
            const table = findTableElement(block) as HTMLTableElement; // guaranteed non-null: flatUnitCount>=2 came from computeRowGroups(table.rows) above
            const tableRowGroups = rowGroups[blockIdx];
            const segmentPlans = segmentPlansByBlock[blockIdx] ?? [];
            tableRowGroups.forEach((rowGroup, g) => {
                unitMeta.push(
                    g === 0
                        ? { element: block, kind: 'block', blockIndex: blockIdx }
                        : {
                              element: table.rows[rowGroup.start],
                              kind: 'row',
                              tableBlock: block,
                              rowIndex: rowGroup.start,
                          },
                );
                const segments = segmentPlans[g];
                if (!segments) return;
                segments.forEach((segment) => {
                    unitMeta.push({
                        element: segment.startChild,
                        kind: 'rowSegment',
                        tableBlock: block,
                        rowIndex: rowGroup.start,
                        cellIndex: segment.cellIndex,
                        cellChildIndex: segment.childIndex,
                    });
                });
            });
            return;
        }

        const lines = lineCounts[blockIdx] ?? 0;
        if (lines >= LINE_ORPHAN_WIDOW_MIN_LINES) {
            for (let l = 0; l < lines; l++) {
                unitMeta.push(
                    l === 0
                        ? { element: block, kind: 'block', blockIndex: blockIdx }
                        : { element: block, kind: 'line', textBlock: block, lineIndex: l },
                );
            }
            return;
        }

        unitMeta.push({ element: block, kind: 'block', blockIndex: blockIdx });
    });
    return unitMeta;
}

/** Shared core result: everything both public projections need. */
interface PageStartCore {
    pageHeightPx: number;
    /** NORMALIZED (gap-free) geometry — see module doc's coordinate-space note. */
    geometry: BlockGeometry[];
    /** Parallel to `geometry` — the SAME units' LIVE (post-gap) offsetTop. */
    liveOffsetTop: number[];
    unitMeta: UnitMeta[];
    startUnitIndices: number[];
    /** Parallel to `startUnitIndices` — the model's raw `unusedPx` per break (RECUR-27304 gap-display), before any kind-based filtering for what actually gets painted. */
    startGaps: number[];
}

/**
 * Shared private core (RECUR-27304 Delta v3): given the editor's top-level
 * blocks and the measured content width, builds the same sticky groups /
 * manual-break flags / row-decomposition the exporter uses, runs the pure
 * `pageBreakModel` fit-loop against the SERVER page height, and returns the
 * page-start UNIT INDICES alongside the flat geometry/metadata needed to
 * project them. `null` when the width isn't measurable (mirrors the previous
 * `EMPTY_BOUNDARIES` early-out).
 *
 * `reservedPageTopPx` (RECUR-27604, default 0 — every pre-existing caller
 * stays byte-identical) is PAGE CHROME: subtracted from the raw SERVER page
 * height BEFORE the finite/positive guard below, so a pathological
 * reservation (>= the raw page height + its own reclaim) degrades to the SAME
 * "no page starts" result an unmeasurable width already produces, rather than
 * a negative page height reaching the fit-loop. See the module doc's
 * coordinate-space note for why this is a capacity adjustment, never an
 * offsetTop addition.
 *
 * RECUR-27604 mismatch fix: whenever the band is actually reserved
 * (`reservedPageTopPx > 0`), `getLogoPageTopReclaimPx(contentWidthPx)` is
 * added back on top of the subtraction — see pageGeometry.ts's own module
 * comment for why (the logo branch's exported page renders at a raised
 * `@page` top margin `getServerPageHeightPx` was never calibrated against, so
 * the plain subtraction alone under-counts the page's real capacity). This is
 * the ONE place both `computePageBoundaries` (overlay) and
 * `computePageStartUnits`/`computePageGapTargets` (export) get their capacity
 * from, so they can never disagree about it. No-logo callers
 * (`reservedPageTopPx === 0`) skip the reclaim entirely — byte-identical to
 * before this fix.
 *
 * `applyRowOffsetCorrection` (RECUR-27604 editor-band flag, default
 * `reservedPageTopPx > 0` — see `buildAtomicUnits`' own doc comment) forwards
 * straight through to `buildAtomicUnits`; it affects `liveOffsetTop` only,
 * never `geometry`/`pageHeightPx`, so it can never change which unit the fit
 * loop picks as a page start.
 */
function computePageStartIndices(
    blocks: HTMLElement[],
    contentWidthPx: number,
    reservedPageTopPx = 0,
    applyRowOffsetCorrection: boolean = reservedPageTopPx > 0,
): PageStartCore | null {
    if (!Number.isFinite(contentWidthPx) || contentWidthPx <= 0) return null;

    // The single shared capacity expression (RECUR-27604): raw SERVER page
    // height, minus the band reservation, plus that same band's page-top-
    // margin reclaim — but ONLY when a band is actually reserved, so the
    // no-logo path (reservedPageTopPx === 0) stays byte-identical to before
    // this fix (getLogoPageTopReclaimPx would otherwise still add a few px
    // even at reservedPageTopPx === 0, which must never happen for a
    // no-logo document).
    const pageHeightPx = Math.max(
        0,
        getServerPageHeightPx(contentWidthPx) - reservedPageTopPx + (reservedPageTopPx > 0 ? getLogoPageTopReclaimPx(contentWidthPx) : 0),
    );
    if (!Number.isFinite(pageHeightPx) || pageHeightPx <= 0 || blocks.length === 0) {
        return { pageHeightPx, geometry: [], liveOffsetTop: [], unitMeta: [], startUnitIndices: [], startGaps: [] };
    }

    // buildPaginationGroups classifies each block by its OWN tagName (H1-H6
    // sticky; TABLE/HR/IMG/FIGURE/UL/OL group-terminating). A table's tagName
    // lives on the <table> one level inside its `.tableScroll` wrapper (see
    // findTableElement) — passing the wrapper itself would leave
    // buildPaginationGroups blind to it (GROUP_END_TAGS never matches a bare
    // `DIV`), letting a sticky heading's lookahead sweep straight past the
    // table into whatever follows it instead of stopping there. Classify off
    // the inner <table> instead; every other block is unchanged.
    const classificationBlocks = blocks.map((block) => findTableElement(block) ?? block);
    const groups = buildPaginationGroups(classificationBlocks);

    // RECUR-27304 Delta v2/v3/v5: decompose any table block into one atomic
    // unit per rowspan-bound row GROUP (ordinarily one per row — see
    // computeRowGroups), and any long-enough plain paragraph into one atomic
    // unit per rendered LINE, then re-map the block-level groups above onto
    // that flat list — see buildAtomicUnits / reindexGroupsForRowsAndLines
    // for why this must be ONE combined pass.
    const { geometry, liveOffsetTop, rowCounts, rowGroups, segmentPlansByBlock, lineCounts, unitStart } = buildAtomicUnits(
        blocks,
        reservedPageTopPx,
        pageHeightPx,
        applyRowOffsetCorrection,
    );
    // RECUR-27604 §14.1G — reindexGroupsForRowsAndLines needs NO change for
    // the oversized-row split: `rowCounts` is now a FLAT UNIT count (row
    // groups + segments), but this function only ever reasons about a COUNT
    // of flat units, never what a unit represents — each extra segment unit
    // simply becomes its own independently breakable one-unit group, exactly
    // what a segment must be (verified against its own `:319-333`).
    const reindexedGroups = reindexGroupsForRowsAndLines(groups, rowCounts, lineCounts, unitStart);

    // `geometry` is NORMALIZED (gap-free — see module doc), so this always
    // runs against the SAME layout regardless of whatever gap is currently
    // painted on the live DOM (RECUR-27304 gap-display) — this is what keeps
    // `computePageStartUnits` byte-identical whether or not `showPageGaps`
    // happens to be on at export time.
    const pageBreaks = pageBreakModelWithGaps(reindexedGroups, geometry, pageHeightPx);
    const unitMeta = buildUnitMeta(blocks, rowCounts, rowGroups, segmentPlansByBlock, lineCounts);

    return {
        pageHeightPx,
        geometry,
        liveOffsetTop,
        unitMeta,
        startUnitIndices: pageBreaks.map((gap) => gap.startIdx),
        startGaps: pageBreaks.map((gap) => gap.unusedPx),
    };
}

/**
 * Glue, exported standalone so the core calc is unit-testable without a
 * ResizeObserver: given the editor's top-level block elements and the
 * measured content width, returns the overlay's boundary Ys — byte-identical
 * shape/values to the pre-Delta-v3 `usePageBoundaries.computePageBoundaries`,
 * now paired with the per-boundary gap (RECUR-27304 gap-display).
 * `boundaries` are LIVE (post-gap) coordinates — see module doc.
 *
 * `reservedPageTopPx` (RECUR-27604, default 0) — see `computePageStartIndices`.
 * `applyRowOffsetCorrection` (RECUR-27604 editor-band flag, default
 * `reservedPageTopPx > 0`) — see `computePageStartIndices`/`buildAtomicUnits`;
 * `usePageBoundaries.ts` passes its own `slotActive` here (RECUR-27604 §11,
 * rev 6: `showPageGaps` alone, unconditionally true whenever the hook
 * measures) so a row-kind boundary's Y carries the correction wherever a mask
 * anchors, without touching the model's own capacity reservation.
 */
export function computePageBoundaries(
    blocks: HTMLElement[],
    contentWidthPx: number,
    reservedPageTopPx = 0,
    applyRowOffsetCorrection?: boolean,
): PageBoundaries {
    const core = computePageStartIndices(blocks, contentWidthPx, reservedPageTopPx, applyRowOffsetCorrection);
    if (!core) return EMPTY_BOUNDARIES;
    return {
        boundaries: core.startUnitIndices.map((idx) => core.liveOffsetTop[idx]),
        // Only a 'block'-kind start ever gets a real DOM gap (see
        // computePageGapTargets — margin has no effect on a <tr>), so a
        // mid-table 'row' start reports 0 here even though the model's own
        // unusedPx is exact — nothing is actually rendered there to paint.
        gaps: core.startUnitIndices.map((idx, i) => (core.unitMeta[idx].kind === 'block' ? core.startGaps[i] : 0)),
        kinds: core.startUnitIndices.map((idx) => core.unitMeta[idx].kind),
        pageHeightPx: core.pageHeightPx,
    };
}

/**
 * The export-time projection (RECUR-27304 Delta v3): finds `contentEl`'s
 * top-level blocks itself (same selector/filter the overlay hook uses),
 * measures its own width, and maps each model page-start unit index back onto
 * the LIVE element(s) it corresponds to — see `PageStartUnit`. Returns `[]`
 * when the width isn't measurable (mirrors `computePageBoundaries`).
 *
 * Unaffected by gap application (RECUR-27304 gap-display): `core.geometry` is
 * NORMALIZED, so `offsetTop` here is the same gap-free value this function
 * has always returned, regardless of whether `showPageGaps` gaps happen to
 * be painted on `contentEl` right now — see the module doc's coordinate-space
 * note. The exporter itself is untouched.
 *
 * `reservedPageTopPx` (RECUR-27604, default 0) — see `computePageStartIndices`.
 */
export function computePageStartUnits(contentEl: HTMLElement, reservedPageTopPx = 0): PageStartUnit[] {
    const blocks = getTopLevelBlocks(contentEl);
    const core = computePageStartIndices(blocks, contentEl.offsetWidth, reservedPageTopPx);
    if (!core) return [];
    return core.startUnitIndices.map((idx) => ({ ...core.unitMeta[idx], offsetTop: core.geometry[idx].offsetTop }));
}

// ── Crossing-content fallback (RECUR-27604 §12, rev 7 — must precede §11) ───
//
// The injected slot + mask only ever push down / mask the ONE table that
// owns the page-start row (or, for a block-kind start, grow that block's own
// margin) — see `applyPageGapStyles`/`PageBreakSlotOverlay`. Anything ELSE
// occupying that same horizontal band (§12.1's repro: a columns block's own
// `.colResizeHandle`, `position: absolute; height: 100%`, painting above
// every overlay) is neither pushed down nor maskable — a full-width mask
// there would hide REAL content, worse than the line it removes. Policy (b):
// a seam whose slot Y is crossed by anything other than its own host is
// treated as UN-HOSTABLE — same terminal state as an inline-`height` row
// (§8.5) or a `'line'`-kind start: no slot, no mask, no edges, no band, bare
// hairline + chip.

/** RECUR-27604 §12.3 — the only DOM shapes that can legitimately occupy a slot's Y range without being part of the slot's own host: another Slate element, or a columns block's own resize-handle/wrapper chrome. */
const CROSSING_CANDIDATE_SELECTOR = '[data-slate-node="element"], [class*="colResizeHandle"], [class*="columnGroup"]';

/** A box that merely STARTS or ENDS exactly at the seam is not "crossing" it (§12.3). */
const CROSSING_LINE_EPS_PX = 0.5;

/**
 * RECUR-27604 §12.3 — strict containment of the LINE `y = slotTop`: the slot
 * has zero height in the gap-free space this runs in (before any --page-gap
 * is painted), so a range test would be meaningless — only whether a
 * candidate's box starts strictly above the seam and ends strictly below it.
 */
function crossesSlotLine(rect: DOMRect, slotTopVp: number): boolean {
    return rect.top < slotTopVp - CROSSING_LINE_EPS_PX && rect.bottom > slotTopVp + CROSSING_LINE_EPS_PX;
}

/** §12.3 point 3 — gutter-only chrome (the "Page N" chip, block drag handles) must never count; `pageWidth <= 0` (unmeasured, e.g. jsdom) never filters a real crossing out. */
function overlapsContentColumnX(rect: DOMRect, pageLeft: number, pageWidth: number): boolean {
    return pageWidth <= 0 || (rect.right > pageLeft && rect.left < pageLeft + pageWidth);
}

/** §12.3 — nodes that belong to THIS unit's own injection vehicle are never "other content": the host table, that table's ancestors within `ownBlock`, and — row-kind only — the padded `<tr>` and its descendants. */
function isPartOfOwnHostChain(node: Element, hostTable: HTMLTableElement | null, paddedRow: HTMLElement | null): boolean {
    if (hostTable && (node === hostTable || node.contains(hostTable))) return true;
    if (paddedRow && (node === paddedRow || paddedRow.contains(node))) return true;
    return false;
}

/**
 * RECUR-27604 §12 — whether anything OTHER than `unit`'s own host crosses the
 * horizontal line at its slot's edge (`unit.getBoundingClientRect().top` for
 * a TOP-slot host — the slot sits ABOVE that edge, §1.2 — or `.bottom` for an
 * END-slot host, RECUR-27604 §13.3: the slot sits BELOW that edge instead, so
 * checking `.top` there would test the WRONG line — a candidate could sit
 * entirely inside `unit`'s own box, spanning the unit's top but not its
 * bottom, and wrongly read as "crossing" a slot that hasn't even been placed
 * near it yet). Runs on the gap-free (stripped) DOM `computePageGapTargets`/
 * `computePageEndGapTargets` already require, over the bounded candidate set
 * §12.3 specifies — never a whole-document walk:
 *   1. every OTHER top-level block (stacked, so only an overlapping/floated/
 *      absolute box can cross one it doesn't own);
 *   2. within `ownBlock` itself, every `[data-slate-node="element"] /
 *      [class*="colResizeHandle"] / [class*="columnGroup"]` descendant that
 *      isn't part of the unit's own host chain (`isPartOfOwnHostChain`) —
 *      without that exclusion the table/wrapper that legitimately spans the
 *      WHOLE seam would always flag itself as crossing it.
 * Both sets are x-filtered to the content column (`overlapsContentColumnX`,
 * §11.3's `pageLeft`/`pageWidth`) so gutter-only chrome can never count. A
 * crossing candidate makes the WHOLE seam un-hostable (§12.2's conservative
 * fallback).
 */
function hasCrossingContent(
    unit: HTMLElement,
    ownBlock: HTMLElement,
    blocks: HTMLElement[],
    pageLeft: number,
    pageWidth: number,
    edge: 'top' | 'bottom' = 'top',
): boolean {
    const unitRect = unit.getBoundingClientRect();
    const slotTopVp = edge === 'top' ? unitRect.top : unitRect.bottom;
    const crosses = (el: Element) => {
        const rect = el.getBoundingClientRect();
        return crossesSlotLine(rect, slotTopVp) && overlapsContentColumnX(rect, pageLeft, pageWidth);
    };

    if (blocks.some((block) => block !== ownBlock && crosses(block))) return true;

    const hostTable = findTableElement(ownBlock);
    const paddedRow = unit !== ownBlock ? unit : null;
    return Array.from(ownBlock.querySelectorAll<HTMLElement>(CROSSING_CANDIDATE_SELECTOR)).some(
        (candidate) => !isPartOfOwnHostChain(candidate, hostTable, paddedRow) && crosses(candidate),
    );
}

/**
 * RECUR-27604 §15.1 — the mid-cell (`'rowSegment'`) analogue of
 * `hasCrossingContent`, for a DIFFERENT reason than sharing its
 * implementation: `hasCrossingContent`'s `isPartOfOwnHostChain` exclusion
 * takes the whole host `<tr>`/primary cell as "part of the vehicle's own
 * chain" — passing that here would exclude every SIBLING cell too (they are
 * DESCENDANTS of the same row), so nothing would ever be checked. Instead:
 * every OTHER cell's own Slate-block children (never the primary cell's —
 * those legitimately continue across the cut, it's the SAME cell), tested
 * against the SAME `crossesSlotLine`/`overlapsContentColumnX` primitives §12
 * already built. `cutChild` is the incoming paragraph itself
 * (`UnitMeta.element` for a `'rowSegment'` unit) — its OWN gap-free top is
 * the cut line. Runs on the STRIPPED (gap-free) DOM, same requirement as
 * every other caller of `computePageGapTargets`/`computePageEndGapTargets`.
 */
export function siblingContentCrossesRowSegmentCut(
    row: HTMLTableRowElement,
    primaryCellIndex: number,
    cutChild: HTMLElement,
    pageLeft: number,
    pageWidth: number,
): boolean {
    const cutYVp = cutChild.getBoundingClientRect().top;
    return Array.from(row.cells).some((cell, ci) => {
        if (ci === primaryCellIndex) return false; // the primary cell's own paragraphs legitimately cross the cut
        return getSlateCellChildren(cell).some((child) => {
            const r = child.getBoundingClientRect();
            return crossesSlotLine(r, cutYVp) && overlapsContentColumnX(r, pageLeft, pageWidth);
        });
    });
}

/**
 * The content column's own left/width, contentEl-relative px (RECUR-27604
 * §11.3; also consumed by §12.3's crossing x-filter above, so the mask and
 * the crossing predicate can never measure "inside the page" differently):
 * `.content`'s padding box — inside its own border, outside its padding.
 * `clientWidth` and computed padding both read 0 in jsdom (no real layout
 * there) — callers treat `width <= 0` as "unmeasured" and fall back
 * accordingly (`overlapsContentColumnX` above; `PageBreakSlotOverlay`'s mask).
 */
export function getContentColumnBox(contentEl: HTMLElement): { left: number; width: number } {
    const style = getComputedStyle(contentEl);
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    const borderLeft = parseFloat(style.borderLeftWidth) || 0;
    return {
        left: contentEl.offsetLeft + borderLeft + padLeft,
        width: contentEl.clientWidth - padLeft - padRight,
    };
}

/**
 * Gap-application targets for the live editor DOM (RECUR-27304 gap-display;
 * extended RECUR-27604 §8 for row-kind hosts; §12 for the crossing-content
 * fallback): every 'block'-kind page-start paired with the vertical space to
 * render immediately above it, PLUS every HOSTABLE 'row'-kind (mid-table)
 * page-start — see `PageGapTarget`. A 'line'-kind start never appears here —
 * no injectable host exists (§0), closed. Consumed by `usePageBoundaries`'s
 * `applyPageGapStyles`; this is ALSO the one and only place the §8.5
 * hostability predicate AND the §12 crossing-content predicate are evaluated
 * (N-5, "one predicate, one place") — `usePageBoundaries` derives `bandTops`
 * straight from the targets returned here rather than re-checking either a
 * second time, so paint, geometry and `bandTops` can never disagree by
 * construction. Recomputes the shared core independently of
 * `computePageBoundaries` (same pattern already used by
 * `computePageStartUnits` above) — cheap: no gap has been applied between
 * the two calls in the same measurement pass, so the second DOM read hits
 * the browser's already-current layout, no extra reflow.
 *
 * `reservedPageTopPx` (RECUR-27604, default 0) — see `computePageStartIndices`.
 */
export function computePageGapTargets(blocks: HTMLElement[], contentWidthPx: number, reservedPageTopPx = 0): PageGapTarget[] {
    const core = computePageStartIndices(blocks, contentWidthPx, reservedPageTopPx);
    if (!core) return [];
    // RECUR-27604 §12.3 — every element in `blocks` comes from
    // `getTopLevelBlocks(contentEl)`, so `blocks[0]`'s own parent already IS
    // `contentEl` — no separate parameter is needed just to measure the
    // content column for the crossing x-filter.
    const contentEl = blocks[0]?.parentElement as HTMLElement | null;
    const { left: pageLeft, width: pageWidth } = contentEl ? getContentColumnBox(contentEl) : { left: 0, width: 0 };
    const targets: PageGapTarget[] = [];
    core.startUnitIndices.forEach((idx, i) => {
        const meta = core.unitMeta[idx];
        if (meta.kind === 'block') {
            // RECUR-27604 §12: a page-start block whose slot Y is crossed by
            // anything other than its own box (§12.1's repro: a columns
            // block's own resize-handle chrome) is un-hostable — same
            // terminal state as a crossed row or a line-kind start.
            if (hasCrossingContent(meta.element, meta.element, blocks, pageLeft, pageWidth)) return;
            targets.push({ element: meta.element, gapPx: core.startGaps[i], kind: 'block', boundaryIndex: i });
            return;
        }
        if (meta.kind === 'row') {
            // onepager-pdf-orphan-blank-page (revises old §8.5): a <tr> with an
            // inline `height` (persisted row size, tableElements.tsx:162) is no
            // longer SKIPPED here. Cell padding on a fixed-height row only
            // partially grows it (the row's own slack absorbs the rest), so the
            // model — which subtracts the FULL slot via getInjectedGapPx — would
            // otherwise measure the row at its NATURAL height, not its fixed
            // one, and re-paginate (drift). usePageBoundaries.applyPageGapStyles
            // instead grows the row's height by EXACTLY the slot (a calc that
            // collapses back to the base under the export's `--page-gap: 0`, so
            // the PDF is untouched); stripPageGapStyles restores the base before
            // every measure, so the model always sees the fixed height.
            // RECUR-27604 §12: a crossed row-kind seam is un-hostable for the
            // same reason as a crossed block-kind one above — reuses the
            // un-hostable path §8.5 already established. `tableBlock` is
            // guaranteed set for every row-kind unit (`buildUnitMeta`).
            if (hasCrossingContent(meta.element, meta.tableBlock as HTMLElement, blocks, pageLeft, pageWidth)) return;
            // The model's own row-kind leftover (`core.startGaps[i]`) is
            // deliberately never painted here — only the reservedPageTopPx
            // band slot is (§8.6); `usePageBoundaries.applyPageGapStyles`
            // adds that addend uniformly on top of every target's `gapPx`.
            targets.push({ element: meta.element, gapPx: 0, kind: 'row', boundaryIndex: i });
            return;
        }
        if (meta.kind === 'rowSegment') {
            // RECUR-27604 §15.0/§15.1/§15.7 — conditionally hosted, revising
            // §14.0's blanket "never targeted": a mid-cell cut gets the SAME
            // full chrome as any other seam iff no SIBLING cell's content
            // crosses the cut line — checked on the OTHER cells only, never
            // the primary cell's own children (which legitimately continue
            // across the cut). `meta.element` already IS the cut child
            // (`buildUnitMeta` sets it to `segment.startChild`) — reused
            // as-is (§15's OPTIONAL fix), no re-derivation.
            const row = findTableElement(meta.tableBlock as HTMLElement)?.rows[meta.rowIndex as number];
            if (!row) return;
            // §8.5 extended to the mid-cell case: a host <tr> carrying an inline
            // `height` (persisted row size / active resize override) absorbs the
            // child's injected padding instead of growing by it, so the model
            // still normalizes the full slot while the DOM never shifts by it —
            // seam and mask drift. Skip, exactly as the 'row' branch above does.
            // Pure read of a style this code never writes; can't feed back into
            // the measurement loop.
            if (row.style.height) return;
            if (siblingContentCrossesRowSegmentCut(row, meta.cellIndex as number, meta.element, pageLeft, pageWidth)) return;
            targets.push({ element: meta.element, gapPx: 0, kind: 'rowSegment', boundaryIndex: i });
        }
        // 'line' — no injectable host (§0), closed: never targeted.
    });
    return targets;
}

// ── Page-END padding (RECUR-27604 §13.3) ────────────────────────────────────

/**
 * §13.3: whether `nextUnit`'s page-start is a LINE-level continuation of
 * `lastUnit`'s own whole-paragraph unit — a line-decomposed paragraph's own
 * `l===0` unit is ALWAYS `kind: 'block'` (§0/Delta v5) with `element` = the
 * WHOLE live `<p>`/DIV, and — whenever it decomposed at all — is ALWAYS
 * immediately followed, in flat-unit order, by `l===1` of the SAME element
 * (a >= 4-line paragraph can never end at line 0). If THAT `l===0` unit is a
 * boundary's own predecessor, `nextUnit` here IS that `l===1` continuation,
 * so a `border-bottom`/`margin-bottom` painted on the WHOLE paragraph would
 * land after ALL its lines, not just after line 0. Detected purely by
 * identity, never by measurement, so it can't feed back into the loop.
 *
 * RECUR-27604 §15.7/SF-2: a `'rowSegment'` `nextUnit` (the mid-cell
 * analogue — a `'block'`/`'row'` unit that is itself the row a §14 oversized-
 * row split decomposed, ending the page right before its OWN continuation)
 * is no longer tested here — it needs the §15.1 sibling-empty guard, which
 * this function has no access to (no `pageLeft`/`pageWidth`, no live DOM
 * walk). `computePageEndGapTargets` below handles it in its OWN dedicated
 * branch, evaluated BEFORE this function is ever called for that boundary.
 */
function isSameLiveContinuation(lastUnit: UnitMeta, nextUnit: UnitMeta): boolean {
    if (nextUnit.kind === 'line') {
        return lastUnit.kind === 'block' && lastUnit.element === nextUnit.textBlock;
    }
    return false;
}

/**
 * Page-END gap-application targets (RECUR-27604 §13.3) — the mirror image of
 * `computePageGapTargets`: for every boundary, the LAST flat unit BEFORE it
 * (`unitMeta[startIdx - 1]`), under the SAME hostability rules as the top
 * slot (§8.5's `row.style.height` skip, §12's crossing-content fallback) PLUS
 * the §14 same-live-continuation exclusion above (`isSameLiveContinuation`).
 * Recomputes the shared core independently, same pattern as
 * `computePageGapTargets` (cheap — no gap applied between the two calls in
 * the same measurement pass).
 *
 * A `'line'`/`'rowSegment'` last unit never appears here — no block-level box
 * at the page's bottom to attach `margin-bottom`/`padding-bottom` to (§13.3's
 * own vehicle table; §14.5 for the rowSegment case specifically).
 *
 * `reservedPageTopPx` (RECUR-27604, default 0) — see `computePageStartIndices`.
 */
/**
 * Resolves the ACTUAL end-of-page host for a `'block'`/`'row'`-kind last unit
 * (RECUR-27604 §13.3 correctness fix; RECUR-27852 last-row fix). The end slot
 * is `padding-bottom` on a `<tr>`'s cells (or `border-bottom` on a block), so
 * the ONLY host that lands the 40px empty slot directly beneath the page's
 * last rendered content — the sole geometry that keeps the seam-relative end
 * mask (`[seam − 40, seam]`, `PageBreakSlotOverlay`) over blank space instead
 * of a live line — is the outgoing page's TRUE LAST `<tr>`.
 *
 * For a table host that is the LAST row of the row GROUP this unit represents
 * (`group.end`), NEVER the group's first row:
 *   • a `'block'`-kind unit is row-GROUP 0 of a multi-group table (its
 *     `element` is the table's own top-level wrapper — "the whole table
 *     moves" is the correct TOP-slot semantics, §1.2/§8 — but for an END slot
 *     a wrapper-level `margin-bottom`/first-row `padding-bottom` would leave
 *     rows 1..`group.end` of that SAME first group rendering BELOW the slot);
 *   • a `'row'`-kind unit is the group starting at `meta.rowIndex`, whose own
 *     `group.end` is likewise the last `<tr>` any rowSpan binds into it.
 * A rowSpan is exactly what makes `group.end > group.start`; the previous code
 * resolved both to the group's FIRST row (`table.rows[0]` / `meta.element`),
 * which for any multi-row group put the padding mid-group with real rows (and
 * their masked-over content) still below it — RECUR-27852's "Forward
 * Contract" repro, where the slot landed on the debt table's top "Name of
 * Bank"/"BOB" rows. `computeRowGroups` (the SAME function `buildAtomicUnits`
 * uses to decompose the table) is authoritative for the group boundaries.
 *
 * A plain table (no rowSpan → one group per row, `group.end === group.start`)
 * is byte-identical to before: the block branch still resolves to
 * `table.rows[0]` for a single-row group 0, and the row branch still resolves
 * to `meta.element`. A `'block'` unit that is NOT a table, or a table with a
 * single group, keeps the whole-block `border-bottom` vehicle.
 */
function resolveEndHost(meta: UnitMeta): { element: HTMLElement; kind: 'block' | 'row'; ownBlock: HTMLElement } {
    if (meta.kind === 'row') {
        const ownBlock = meta.tableBlock as HTMLElement;
        const table = findTableElement(ownBlock);
        // `meta.rowIndex` is always a group's own `start` (buildUnitMeta), so
        // the group is found for every well-formed row unit; `?? meta.element`
        // is a defensive no-op that preserves the pre-fix element otherwise.
        const group = table ? computeRowGroups(table.rows).find((g) => g.start === meta.rowIndex) : undefined;
        return { element: group && table ? table.rows[group.end] : meta.element, kind: 'row', ownBlock };
    }
    const table = findTableElement(meta.element);
    if (table && table.rows.length >= 2) {
        const groups = computeRowGroups(table.rows);
        if (groups.length >= 2) {
            return { element: table.rows[groups[0].end], kind: 'row', ownBlock: meta.element };
        }
    }
    return { element: meta.element, kind: 'block', ownBlock: meta.element };
}

export function computePageEndGapTargets(blocks: HTMLElement[], contentWidthPx: number, reservedPageTopPx = 0): PageEndGapTarget[] {
    const core = computePageStartIndices(blocks, contentWidthPx, reservedPageTopPx);
    if (!core) return [];
    const contentEl = blocks[0]?.parentElement as HTMLElement | null;
    const { left: pageLeft, width: pageWidth } = contentEl ? getContentColumnBox(contentEl) : { left: 0, width: 0 };
    const targets: PageEndGapTarget[] = [];
    core.startUnitIndices.forEach((startIdx, i) => {
        const lastIdx = startIdx - 1;
        if (lastIdx < 0) return; // boundary 0's own "page above" always has at least one unit in practice, but defensive
        const meta = core.unitMeta[lastIdx];
        const nextMeta = core.unitMeta[startIdx];
        // RECUR-27604 §15.7/SF-2 — handled FIRST, before the `meta.kind`
        // guard below: a boundary whose START unit is `'rowSegment'` covers
        // BOTH the 2-page cell (`meta` is the row's own `'row'`/`'block'`-kind
        // seg-0 unit) and the 3+-page cell (`meta` is itself an EARLIER
        // `'rowSegment'` of the SAME cell) uniformly — the end host is always
        // the OUTGOING page's LAST paragraph of that SAME primary cell,
        // `getSlateCellChildren(primaryCell)[nextMeta.cellChildIndex − 1]`,
        // never `meta.element` (which, for the 3+-page case, is the PREVIOUS
        // segment's START child, not this page's own last one). Sequenced
        // ahead of the `meta.kind !== 'block' && meta.kind !== 'row'`
        // early-return so the 3+-page case (`meta.kind === 'rowSegment'`)
        // isn't skipped outright.
        if (nextMeta.kind === 'rowSegment') {
            const row = findTableElement(nextMeta.tableBlock as HTMLElement)?.rows[nextMeta.rowIndex as number];
            const primaryCell = row?.cells[nextMeta.cellIndex as number];
            const cutChild = primaryCell ? getSlateCellChildren(primaryCell)[nextMeta.cellChildIndex as number] : undefined;
            if (!row || !primaryCell || !cutChild) return;
            // §8.5 — see computePageGapTargets' rowSegment branch: a fixed-height
            // host <tr> absorbs the child padding, so painting an end slot here
            // would drift the seam/mask. Skip, mirroring the 'row' end-host guard.
            if (row.style.height) return;
            if (siblingContentCrossesRowSegmentCut(row, nextMeta.cellIndex as number, cutChild, pageLeft, pageWidth)) return;
            const endChild = getSlateCellChildren(primaryCell)[(nextMeta.cellChildIndex as number) - 1];
            if (!endChild) return;
            targets.push({ element: endChild, kind: 'rowSegment', boundaryIndex: i });
            return;
        }
        if (isSameLiveContinuation(meta, nextMeta)) return;
        // 'line' — no block-level box at the page's bottom (§13.3). Never targeted.
        if (meta.kind !== 'block' && meta.kind !== 'row') return;
        const host = resolveEndHost(meta);
        if (host.kind === 'row') {
            if (host.element.style.height) return; // §8.5
            if (hasCrossingContent(host.element, host.ownBlock, blocks, pageLeft, pageWidth, 'bottom')) return;
            targets.push({ element: host.element, kind: 'row', boundaryIndex: i });
            return;
        }
        if (hasCrossingContent(host.element, host.ownBlock, blocks, pageLeft, pageWidth, 'bottom')) return;
        targets.push({ element: host.element, kind: 'block', boundaryIndex: i });
    });
    return targets;
}
